import { useIntl } from 'react-intl';
import { Chip, Tooltip } from '@mui/material';
import type { ChipProps } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

type BankUsageChipProps = {
  assignmentCount: number;
  bankLimitUsageCount?: number;
  bankLimit?: number;
  onClick?: () => void;
  actionLabel?: string;
};

const BankUsageChip = ({
  assignmentCount,
  bankLimitUsageCount = assignmentCount,
  bankLimit,
  onClick,
  actionLabel,
}: BankUsageChipProps) => {
  const intl = useIntl();
  const hasLimit =
    typeof bankLimit === 'number' && Number.isFinite(bankLimit) && Number.isSafeInteger(bankLimit) && bankLimit >= 0;
  const countLabel = intl.formatMessage(
    { id: 'report.bankAssignmentCount', defaultMessage: '{count} banka ataması' },
    { count: assignmentCount }
  );
  const limitLabel = hasLimit
    ? intl.formatMessage(
        {
          id: 'report.bankUsageWithLimit',
          defaultMessage: '{current} / {limit} pasif olmayan banka ataması',
        },
        { current: bankLimitUsageCount, limit: bankLimit }
      )
    : countLabel;

  let color: ChipProps['color'] = assignmentCount > 0 ? 'primary' : 'default';

  if (hasLimit && bankLimitUsageCount > bankLimit) color = 'error';
  else if (hasLimit && bankLimitUsageCount === bankLimit) color = 'warning';

  const label = hasLimit ? `${bankLimitUsageCount} / ${bankLimit}` : countLabel;
  const tooltipTitle = actionLabel ? `${limitLabel} · ${actionLabel}` : limitLabel;

  return (
    <Tooltip title={tooltipTitle}>
      <Chip
        icon={<AccountBalanceIcon />}
        label={label}
        size="small"
        color={color}
        variant={assignmentCount > 0 ? 'filled' : 'outlined'}
        clickable={Boolean(onClick)}
        onClick={
          onClick
            ? (event) => {
                event.stopPropagation();
                onClick();
              }
            : undefined
        }
        aria-label={tooltipTitle}
        sx={{
          height: 26,
          fontSize: '0.75rem',
          fontWeight: 600,
          '& .MuiChip-icon': { fontSize: 17 },
        }}
      />
    </Tooltip>
  );
};

export default BankUsageChip;
